import React, { useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../CommonComponents/Header';
import Sidebar from '../CommonComponents/Sidebar';
import styles from './css/Layout.module.css';

// Create context for sidebar state
const SidebarContext = createContext();

// Custom hook to use sidebar context
export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

// Provider component
export const SidebarProvider = ({ children }) => {
    // Initialize sidebar state from localStorage or default to true
    const [expandSidebar, setExpandSidebar] = useState(() => {
        const saved = localStorage.getItem('expandSidebar');
        return saved ? JSON.parse(saved) : true;
    });

    // Save sidebar state to localStorage whenever it changes
    React.useEffect(() => {
        localStorage.setItem('expandSidebar', JSON.stringify(expandSidebar));
    }, [expandSidebar]);

    const value = {
        expandSidebar,
        setExpandSidebar,
    };

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

// Persistent Layout component that doesn't unmount
export const PersistentLayout = ({ children }) => {
    const { expandSidebar, setExpandSidebar } = useSidebar();
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const location = useLocation();

    const isWorkspaceSelectionScreen =
        location?.pathname === '/select-workspace' ||
        location?.pathname === '/no-workspace-access' ||
        location?.pathname === '/workspace-pulse' ||
        location?.pathname.startsWith('/workspace-settings') ||
        location?.pathname.startsWith('/workspace-setup');

    React.useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div>
            <Header minimal={isWorkspaceSelectionScreen} hideProjectItems={isWorkspaceSelectionScreen} />
            <div className="d-flex">
                {!isWorkspaceSelectionScreen && <Sidebar expandSidebar={expandSidebar} setExpandSidebar={setExpandSidebar} />}
                <div
                    style={{
                        width: isWorkspaceSelectionScreen ? '100vw' : `calc(100vw - ${expandSidebar ? 188 : 48}px)`,
                        transition: 'width 0.2s',
                    }}
                >
                    <div className={styles.viewHeight}>
                        <div className="container-fluid">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Legacy Layout component for backward compatibility
function Layout(props) {
    const { expandSidebar, setExpandSidebar } = useSidebar();
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    React.useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div>
            <Header />

            <div className="d-flex">
                <Sidebar expandSidebar={expandSidebar} setExpandSidebar={setExpandSidebar} />
                <div style={{ width: `calc(100vw - ${expandSidebar ? 188 : 48}px)`, transition: 'width 0.2s' }}>
                    {props.container ? (
                        <div className={styles.viewHeight}>
                            <div className="container-fluid">{props.children}</div>
                        </div>
                    ) : (
                        props.children
                    )}
                </div>
            </div>
        </div>
    );
}

export default Layout;
